import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  InternalPage,
  PageNotFound,
  ResetFormHandler,
} from 'theSource/components';
import { useApiMutation } from 'theSource/utils';
import { ROUTES } from 'theSource/app/routes';
import { mapContactToEditFormValues } from './utils';
import {
  Contacts,
  ContactUpdateRequestDto,
  ContactUpdateResponseDto,
} from '../types';
import {
  EDIT_CONTACT_QUERY_KEY,
  GET_CONTACTS_QUERY_KEY,
  mapFormValuesToContactUpdateRequest,
} from '../utils';
import { PageTitleStyled } from './ContactDetailsEditPageStyled';
import {
  ADD_EDIT_CONTACT_FORM_INITIAL_VALUES,
  AddEditContactForm,
  AddEditContactFormValues,
} from '../components';

export interface ContactDetailsEditPageProps {
  data: Contacts;
  existingGroups: string[];
}

export const ContactDetailsEditPage = ({
  data,
  existingGroups,
}: ContactDetailsEditPageProps) => {
  const { t } = useTranslation('pages.contacts');
  const { contactId } = useParams();
  const navigate = useNavigate();

  const contact = useMemo(
    () => data.userContacts.find((item) => item.id.toString() === contactId),
    [data, contactId]
  );

  const contactDetailsPath = `${ROUTES.additional.profile.contacts.path}/${contactId}`;

  const { isLoading, mutate: updateContact } = useApiMutation<
    ContactUpdateResponseDto,
    {},
    ContactUpdateRequestDto
  >({
    queryKey: EDIT_CONTACT_QUERY_KEY,
    queryKeysToInvalidate: GET_CONTACTS_QUERY_KEY,
    invalidateQueriesCallback: () => navigateBackToContact(),
    method: 'put',
    endpoint: '/contacts?ps_new_ui=true',
    snackbarOptions: {
      position: 'aboveMenu',
    },
  });

  const navigateBackToContact = () => navigate(contactDetailsPath);

  if (!contact) {
    return <PageNotFound />;
  }

  const mappedContact = {
    ...ADD_EDIT_CONTACT_FORM_INITIAL_VALUES,
    ...mapContactToEditFormValues(contact, existingGroups),
  };
  const initialValues: AddEditContactFormValues =
    mappedContact.phones && mappedContact.phones?.length > 0
      ? mappedContact
      : {
          ...mappedContact,
          phones: ADD_EDIT_CONTACT_FORM_INITIAL_VALUES.phones,
        };

  const handleSubmit = (
    values: AddEditContactFormValues,
    resetForm: ResetFormHandler
  ) =>
    updateContact(mapFormValuesToContactUpdateRequest(values, contact), {
      onSuccess: () => resetForm(),
    });

  return (
    <InternalPage
      title={t('contacts')}
      withBackLink
      backLinkPath={contactDetailsPath}
      testId="contact-details-edit-page"
    >
      <PageTitleStyled className="headline-3">
        {t('editContactPageTitle')}
      </PageTitleStyled>
      <AddEditContactForm
        isLoading={isLoading}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={navigateBackToContact}
      />
    </InternalPage>
  );
};
